import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HomeIcon from "../../../assets/HomeIcon.svg";
import InfoIcon from "../../../assets/InfoIcon.svg";
import { DATE_FORMAT } from "../../../constants/app-constant";
import { danger, success } from "../../../constants/color-constants";
import {
  EASTERN_TIME_ZONE,
  OPCD_API_STATUS,
  OPCD_RESPONSE_STATUS,
} from "../../../constants/opcd-constants";
import { RouteConstants } from "../../../constants/route-constant";
import {
  fileToDataUrl,
  useOpcdQueryDetail,
  useSubmitOpcdQuery,
} from "../../../hooks/queries";
import { useFhirClient } from "../../../hooks/useFhirClient";
import { useAppSelector } from "../../../store/hooks";
import useColors from "../../../theme/useColors";
import type {
  OpcdClinicalIndicator,
  OpcdClinicalReference,
  OpcdQueryOption,
  OpcdQueryRequest,
} from "../../../types/opcd-queries";
import { formatDate, formatTimeWithZoneAbbreviation } from "../../../utils";
import {
  buildOpcdSubmitOptions,
  extractOpcdQueryDetail,
  extractOpcdResponseSelections,
  formatOpcdClinicalIndicatorLine,
  getOpcdDocumentedHeading,
  groupOpcdCdiIndicators,
  groupOpcdProFeeIndicators,
  isOpcdOtherOption,
  isOpcdOutpatientCdiQuery,
  isOpcdQueryCompleted,
  joinOpcdReferenceTexts,
} from "../../../utils/opcd-query-utils";
import {
  getHomeScreenSignatureDefault,
  getHomeScreenUserName,
  getProviderSalutation,
} from "../../../utils/provider-display-utils";
import { CheckboxGroup } from "../../Shared/CheckboxGroup";
import SignatureFooter from "../../Shared/SignatureFooter";
import SignatureInputDialog from "../../Shared/SignatureInputDialog";
import { InfoPanel } from "../../Shared/infopanel/infopanel";

const bulwarkLogo = "/bulwark_logo-new.png";
const arcLogo = "/arcplus-primary.svg";

const EMPTY_REFERENCE_PLACEHOLDER = "________________________________";

const clinicalPaperStyle = (colors: ReturnType<typeof useColors>) => ({
  backgroundColor: colors.gray[2],
  border: `1px solid ${colors.gray[3]}`,
});

const IndicatorLines = ({
  items,
  heading,
}: {
  items: OpcdClinicalIndicator[];
  heading?: string;
}) => {
  const colors = useColors();
  if (!items.length) return null;
  return (
    <Box>
      {heading ? (
        <Text
          size="14px"
          fw={900}
          lh="24px"
          c={colors.gray[8]}
          style={{ fontFamily: "Roboto, sans-serif" }}
        >
          {heading}:
        </Text>
      ) : null}
      <Stack gap="2px">
        {items.map((item, index) => (
          <Text
            key={`${heading ?? "indicator"}-${index}-${item.name ?? item.testName ?? "item"}`}
            size="12px"
            fw={400}
            lh="20px"
            c={colors.gray[8]}
            style={{ fontFamily: "Roboto, sans-serif" }}
          >
            {index + 1}. {formatOpcdClinicalIndicatorLine(item)}
          </Text>
        ))}
      </Stack>
    </Box>
  );
};

const EvidenceList = ({
  evidences,
  dateOfService,
}: {
  evidences: OpcdClinicalReference[];
  dateOfService?: string;
}) => {
  const colors = useColors();
  return (
    <>
      <Text
        size="14px"
        fw={900}
        lh="24px"
        c={colors.gray[8]}
        style={{ fontFamily: "Roboto, sans-serif" }}
      >
        Relevant clinical documentation references are:
      </Text>
      {evidences.map((evidence, index) => (
        <Text
          key={index}
          size="12px"
          fw={400}
          lh="20px"
          c={colors.gray[8]}
          style={{ fontFamily: "Roboto, sans-serif" }}
        >
          {index + 1}. &quot;{evidence.text}&quot;
          {dateOfService ? ` (DOS: ${dateOfService})` : ""}
          {evidence.page ? ` (Page ${evidence.page})` : ""}
        </Text>
      ))}
    </>
  );
};

const CdiDocumentedAndEvidences = ({
  heading,
  code,
  description,
  indicatorGroups,
  evidences,
  dateOfService,
}: {
  heading: string;
  code: string;
  description?: string;
  indicatorGroups: ReturnType<typeof groupOpcdCdiIndicators>;
  evidences: OpcdClinicalReference[];
  dateOfService?: string;
}) => {
  const colors = useColors();
  const hasEvidences = evidences.length > 0;

  return (
    <SimpleGrid cols={{ base: 1, md: hasEvidences ? 2 : 1 }} spacing="xs">
      <Paper
        radius="sm"
        px="md"
        py="4px"
        h="100%"
        style={{
          backgroundColor: colors.gray[2],
          border: `1px solid ${colors.gray[1]}`,
        }}
      >
        <Text size="12px" lh="20px" fw={400} c={colors.gray[7]}>
          <Text
            span
            c={colors.gray[8]}
            style={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              fontWeight: 900,
              lineHeight: "24px",
            }}
          >
            {heading}:
          </Text>
          <Text span style={{ marginLeft: "12px" }}>
            {code} - {description || "-"}
          </Text>
        </Text>
        <Stack gap="4px" mt="4px">
          {indicatorGroups.map((group) => (
            <IndicatorLines
              key={group.type}
              heading={group.heading}
              items={group.items}
            />
          ))}
        </Stack>
      </Paper>
      {hasEvidences ? (
        <Paper
          radius="sm"
          px="md"
          py="4px"
          h="100%"
          style={clinicalPaperStyle(colors)}
        >
          <EvidenceList
            evidences={evidences}
            dateOfService={dateOfService}
          />
        </Paper>
      ) : null}
    </SimpleGrid>
  );
};

const ProFeeClinicalGrid = ({ request }: { request: OpcdQueryRequest }) => {
  const colors = useColors();
  const groups = groupOpcdProFeeIndicators(request.clinicalIndicators);
  const riskText = joinOpcdReferenceTexts(request.riskFactors);
  const treatmentText = joinOpcdReferenceTexts(request.treatments);

  return (
    <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xs">
      <Paper radius="sm" px="md" py="4px" h="100%" style={clinicalPaperStyle(colors)}>
        {groups.length ? (
          <Stack gap="4px">
            {groups.map((group) => (
              <IndicatorLines
                key={group.type}
                heading={group.heading}
                items={group.items}
              />
            ))}
          </Stack>
        ) : (
          <Text
            size="12px"
            fw={400}
            lh="20px"
            c={colors.gray[8]}
            style={{ fontFamily: "Roboto, sans-serif" }}
          >
            See clinical documentation references below.
          </Text>
        )}
      </Paper>
      <Paper radius="sm" px="md" py="4px" h="100%" style={clinicalPaperStyle(colors)}>
        <Text
          size="14px"
          fw={900}
          lh="24px"
          c={colors.gray[8]}
          style={{ fontFamily: "Roboto, sans-serif" }}
        >
          Risk Factors:
        </Text>
        <Text
          size="12px"
          fw={400}
          lh="20px"
          c={colors.gray[8]}
          style={{ fontFamily: "Roboto, sans-serif" }}
        >
          {riskText || EMPTY_REFERENCE_PLACEHOLDER}
        </Text>
      </Paper>
      <Paper radius="sm" px="md" py="4px" h="100%" style={clinicalPaperStyle(colors)}>
        <Text
          size="14px"
          fw={900}
          lh="24px"
          c={colors.gray[8]}
          style={{ fontFamily: "Roboto, sans-serif" }}
        >
          Treatment:
        </Text>
        <Text
          size="12px"
          fw={400}
          lh="20px"
          c={colors.gray[8]}
          style={{ fontFamily: "Roboto, sans-serif" }}
        >
          {treatmentText || EMPTY_REFERENCE_PLACEHOLDER}
        </Text>
      </Paper>
    </SimpleGrid>
  );
};

const OpcdQueryDetails: React.FC = () => {
  const colors = useColors();
  const navigate = useNavigate();
  const { state } = useLocation() as {
    state?: { from?: string; moduleSlug?: string };
  };
  const { query_id } = useParams();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery("(max-width: 760px)");
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const contentRef = useRef<HTMLDivElement>(null);
  const provider = useAppSelector((store) => store.provider.provider);
  const providerNPI = useAppSelector((store) => store.provider.providerNPI);
  const practitioner = useAppSelector((store) => store.fhir.practitioner);
  const client = useFhirClient();
  const previousRoute = state?.from || RouteConstants.ROUTE_OPCD_QUERIES;

  const { data: query, isLoading, isError } = useOpcdQueryDetail(
    query_id,
    providerNPI
  );
  const submitMutation = useSubmitOpcdQuery();
  const [localQuery, setLocalQuery] = useState(query);
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [otherText, setOtherText] = useState("");
  const [signatureFile, setSignatureFile] = useState<File | string | null>(null);
  const [signedValue, setSignedValue] = useState("");
  const [formTouched, setFormTouched] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [backModalOpen, setBackModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (query) setLocalQuery(query);
  }, [query]);

  useEffect(() => {
    if (!localQuery) return;
    const saved = extractOpcdResponseSelections(localQuery.response);
    setSelectedOptionIds(saved.selectedIds);
    setOtherText(saved.otherText);
    setSignatureFile(localQuery.response?.signatureUrl ?? null);
  }, [localQuery]);

  const providerInfo = provider?.provider_info;
  const providerName = useMemo(
    () =>
      getHomeScreenUserName(providerInfo, client, practitioner) ||
      localQuery?.request?.provider?.name ||
      "",
    [client, localQuery?.request?.provider?.name, practitioner, providerInfo]
  );
  const signatureDefault = useMemo(
    () => getHomeScreenSignatureDefault(providerInfo, client, practitioner),
    [client, practitioner, providerInfo]
  );
  const providerSalutation = useMemo(
    () => getProviderSalutation(providerInfo, practitioner),
    [practitioner, providerInfo]
  );
  const signedProviderName = useMemo(() => {
    if (
      !providerSalutation ||
      providerName.toLowerCase().includes(providerSalutation.toLowerCase())
    ) {
      return providerName;
    }
    return `${providerName}, ${providerSalutation}`;
  }, [providerName, providerSalutation]);
  const salutationName = providerName.replace(/^dr\.?\s+/i, "");

  const request = localQuery?.request;
  const moduleSlug = localQuery?.moduleSlug || state?.moduleSlug;
  const isOutpatientCdi = isOpcdOutpatientCdiQuery(moduleSlug);
  const documentedHeading = getOpcdDocumentedHeading(
    moduleSlug,
    localQuery?.codeType
  );
  const cdiIndicatorGroups = useMemo(
    () => groupOpcdCdiIndicators(request?.clinicalIndicators),
    [request?.clinicalIndicators]
  );
  const questionOptions = useMemo(() => {
    const options = request?.options?.length
      ? request.options
      : request?.selectedOptions ?? [];
    return [...options].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    );
  }, [request?.options, request?.selectedOptions]);
  const selectedLabels = selectedOptionIds
    .map((id) =>
      questionOptions.find((option) => String(option.id) === id)
    )
    .filter((option): option is OpcdQueryOption => Boolean(option))
    .map((option) => option.label);
  const otherLabels = questionOptions
    .filter(isOpcdOtherOption)
    .map((option) => option.label);
  const hasOtherSelected = selectedOptionIds.some((id) => {
    const option = questionOptions.find((item) => String(item.id) === id);
    return Boolean(option && isOpcdOtherOption(option));
  });
  const completed = isOpcdQueryCompleted(
    localQuery?.status,
    localQuery?.response
  );
  const formValid =
    selectedOptionIds.length > 0 && (!hasOtherSelected || Boolean(otherText.trim()));

  const handleSelectionChange = (labels: string[]) => {
    setSelectedOptionIds(
      labels
        .map((label) => questionOptions.find((item) => item.label === label))
        .filter((item): item is OpcdQueryOption => Boolean(item))
        .map((item) => String(item.id))
    );
    setFormTouched(true);
  };

  const handleSubmit = async () => {
    if (
      !query_id ||
      !providerNPI ||
      !localQuery ||
      !formValid ||
      !signatureFile
    )
      return;
    setSubmitError("");
    try {
      const signatureUrl =
        signatureFile instanceof File
          ? await fileToDataUrl(signatureFile)
          : signatureFile;
      const body = {
        providerNpi: providerNPI,
        selectedOptions: buildOpcdSubmitOptions(
          questionOptions,
          selectedOptionIds,
          otherText
        ),
        ...(hasOtherSelected && otherText.trim()
          ? { otherJustification: otherText.trim() }
          : {}),
        ...(signatureUrl ? { signatureUrl } : {}),
        status: OPCD_RESPONSE_STATUS.COMPLETED,
      };

      const result = await submitMutation.mutateAsync({
        queryId: query_id,
        body,
      });
      const updated = extractOpcdQueryDetail(result.data);
      setLocalQuery(
        updated ?? {
          ...localQuery,
          status: OPCD_API_STATUS.COMPLETED,
          response: body,
          respondedAt: new Date().toISOString(),
        }
      );
      setFormTouched(false);
      contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to submit OPCD query:", error);
      setSubmitError("Unable to submit the query. Please try again.");
    }
  };

  const handleBack = () => {
    if (formTouched) {
      setBackModalOpen(true);
    } else {
      navigate(previousRoute);
    }
  };

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center">
        <Text c="dimmed">Loading query...</Text>
      </Flex>
    );
  }

  if (isError || !localQuery) {
    return (
      <Flex h="100vh" direction="column" gap="md" align="center" justify="center">
        <Text c="red">Unable to load this query.</Text>
        <Button variant="outline" onClick={() => navigate(previousRoute)}>
          Back to CDI Queries
        </Button>
      </Flex>
    );
  }

  const patient = request?.patient;
  const formattedDateOfService = patient?.dateOfService
    ? formatDate(
        patient.dateOfService,
        DATE_FORMAT.MM_DD_YYYY,
        EASTERN_TIME_ZONE
      )
    : "";
  const sentDate = localQuery.sentAt || localQuery.createdAt;
  const querySentBy =
    request?.headers?.section3
      ?.match(/please contact:\s*([^\n]+)/i)?.[1]
      ?.trim() || "-";
  const signatureInfo = localQuery.respondedAt
    ? { UPDATED_DATE: localQuery.respondedAt }
    : undefined;

  return (
    <Paper
      shadow="xs"
      pt="2px"
      withBorder
      style={{
        overflow: "hidden",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        px="4px"
        pt="8px"
        wrap="nowrap"
        style={{ overflowX: "auto", flexShrink: 0 }}
      >
        <Box
          component="img"
          src={arcLogo}
          style={{ height: isSmallScreen ? 18 : 20, width: "auto" }}
        />
        <Flex justify="space-between" align="center" gap="xs" px="xs" flex={1}>
          <Group gap="xs" wrap="nowrap">
            <Text c="dimmed" size="12px">
              Query ID:
            </Text>
            <Text fw={500} size="12px">
              {localQuery.queryId}
            </Text>
          </Group>
          <Group
            gap={isMobile ? "4px" : "xs"}
            style={{
              flexWrap: isMobile ? "wrap" : "nowrap",
              flexGrow: 0.25,
              alignItems: "flex-start",
              justifyContent: "flex-start",
            }}
          >
            <Badge
              color={completed ? success[6] : danger[4]}
              variant="light"
              radius="sm"
              size="xs"
              style={{
                border: `1px solid ${completed ? success[6] : danger[4]}`,
                height: isMobile ? "22px" : "30px",
                marginTop: isMobile ? "1px" : "unset",
                lineHeight: "22px",
                padding: "0 4px",
                minWidth: "75px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                fontSize: isMobile ? "10px" : "14px",
              }}
            >
              {completed ? "Completed" : "Edit Mode"}
            </Badge>
          </Group>
          <Button
            variant="outline"
            size={isMobile ? "compact-xs" : "xs"}
            onClick={handleBack}
            c={colors.gray[8]}
          >
            Home
            <Box component="img" src={HomeIcon} ml="6px" h="16px" w="16px" />
          </Button>
        </Flex>
        <Box
          component="img"
          src={bulwarkLogo}
          style={{ height: isSmallScreen ? 22 : 28, width: "auto" }}
        />
      </Flex>

      <Divider my="6px" />

      <Box mb="6px" px="8px" style={{ flexShrink: 0 }}>
        <InfoPanel
          data={{
            "Patient Name": patient?.name || "-",
            DOB: patient?.dob
              ? formatDate(
                  patient.dob,
                  DATE_FORMAT.MM_DD_YYYY,
                  EASTERN_TIME_ZONE
                ) || "-"
              : "-",
            Gender: patient?.gender || "-",
            DOS: formattedDateOfService || "-",
            "Visit ID / MRN": patient?.visitId || patient?.mrn || "-",
          }}
        />
      </Box>

      <Stack
        ref={contentRef}
        gap="8px"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "0 8px 8px",
        }}
      >
        <Paper
          radius="sm"
          px="md"
          py="4px"
          style={{
            backgroundColor: colors.gray[2],
            border: `1px solid ${colors.gray[1]}`,
          }}
        >
          <Text size="14px" lh="20px" fw={500} c={colors.gray[8]}>
            Dear{" "}
            <Text
              span
              style={{
                backgroundColor: colors.warning[2],
                padding: "0 2px",
                borderRadius: "2px",
              }}
            >
              Dr. {salutationName}
            </Text>
          </Text>
          {request?.headers?.section2 && (
            <Text
              size="12px"
              lh="18px"
              fw={400}
              c={colors.gray[7]}
              mt="4px"
            >
              {request.headers.section2}
            </Text>
          )}
        </Paper>

        {isOutpatientCdi ? (
          <CdiDocumentedAndEvidences
            heading={documentedHeading}
            code={localQuery.code}
            description={request?.description}
            indicatorGroups={cdiIndicatorGroups}
            evidences={request?.evidences ?? []}
            dateOfService={formattedDateOfService}
          />
        ) : (
          <>
            <Paper
              radius="sm"
              px="md"
              py="4px"
              style={{
                backgroundColor: colors.gray[2],
                border: `1px solid ${colors.gray[1]}`,
              }}
            >
              <Text size="12px" lh="20px" fw={400} c={colors.gray[7]}>
                <Text
                  span
                  c={colors.gray[8]}
                  style={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    fontWeight: 900,
                    lineHeight: "24px",
                  }}
                >
                  {documentedHeading}:
                </Text>
                <Text span style={{ marginLeft: "12px" }}>
                  {localQuery.code} - {request?.description || "-"}
                </Text>
              </Text>
            </Paper>
            {request ? <ProFeeClinicalGrid request={request} /> : null}
            {request?.evidences?.length ? (
              <Paper
                radius="sm"
                px="md"
                py="4px"
                style={clinicalPaperStyle(colors)}
              >
                <EvidenceList
                  evidences={request.evidences}
                  dateOfService={formattedDateOfService}
                />
              </Paper>
            ) : null}
          </>
        )}

        {questionOptions.length ? (
          <CheckboxGroup
            title="Based on the clinical indicators and your professional judgment Please complete by selecting one of the options below."
            options={questionOptions.map((option) => option.label)}
            specifyBelowForOptions={otherLabels}
            required
            onChange={handleSelectionChange}
            onOtherChange={(value) => {
              setOtherText(value);
              setFormTouched(true);
            }}
            selectedOptions={selectedLabels}
            otherText={otherText}
            isPreviewMode={completed}
          />
        ) : (
          <Text size="12px" c="dimmed">
            No response options are available for this query.
          </Text>
        )}

        <SignatureFooter
          signatureFile={signatureFile}
          providerName={signedProviderName}
          signedValue={signedValue}
          isPreviewMode={completed}
          disableSignButton={!formValid || completed}
          handleSubmit={handleSubmit}
          onSignQuery={() => setSignatureModalOpen(true)}
          signInDisabled={!formValid || !signatureFile || completed}
          hideSaveAndPreview
          hideSubmit={completed}
          signatureInfo={signatureInfo}
          submitLoading={submitMutation.isPending}
          saveLoading={false}
          timezoneName={EASTERN_TIME_ZONE}
          timezoneAbbrev="ET"
          plainSignerName
          signatureDateFontSize="14px"
        />
        {submitError && (
          <Text size="12px" c="red" role="alert">
            {submitError}
          </Text>
        )}

        <Paper
          radius="sm"
          px="md"
          py="4px"
          style={{
            backgroundColor: colors.gray[2],
            border: `1px solid ${colors.gray[3]}`,
          }}
        >
          <Text size="12px" lh={1.5}>
            <Text span fw={500}>
              Disclaimer:
            </Text>{" "}
            <Text span c={colors.gray[7]} fw={400}>
              {request?.headers?.disclaimer || "-"}
            </Text>
          </Text>
        </Paper>

        <Group gap="xl" px="4px">
          <Text size="12px" fw={600} lh="22px" c={colors.gray[8]}>
            Query Sent By: {querySentBy}
          </Text>
          <Text size="12px" fw={600} lh="22px" c={colors.gray[8]}>
            On:{" "}
            {formatDate(sentDate, DATE_FORMAT.MM_DD_YYYY, EASTERN_TIME_ZONE)} at{" "}
            {formatTimeWithZoneAbbreviation(
              sentDate,
              EASTERN_TIME_ZONE,
              "ET"
            )}
          </Text>
        </Group>
      </Stack>

      <SignatureInputDialog
        initialValue={signatureDefault}
        opened={signatureModalOpen}
        onCancel={() => setSignatureModalOpen(false)}
        onSubmit={(signature, file) => {
          setSignedValue(signature);
          setSignatureFile(file);
          setSignatureModalOpen(false);
          setFormTouched(true);
        }}
      />

      <Modal
        withCloseButton={false}
        opened={backModalOpen}
        onClose={() => setBackModalOpen(false)}
        centered
      >
        <Stack gap="sm">
          <Group justify="center">
            <Box component="img" src={InfoIcon} h={20} w={20} />
            <Text fw={600}>Warning!!</Text>
          </Group>
          <Text size="sm" ta="center">
            You have unsaved changes. Are you sure you want to leave?
          </Text>
          <Group justify="center">
            <Button variant="outline" onClick={() => setBackModalOpen(false)}>
              Stay
            </Button>
            <Button onClick={() => navigate(previousRoute)}>Leave</Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
};

export default OpcdQueryDetails;
